import { useEffect, useRef, useCallback } from "react";

/**
 * Observes a sentinel element and calls `onLoadMore` when it enters the viewport.
 *
 * @param {Object} options
 * @param {boolean} options.hasMore  - Whether more items are available to load.
 * @param {boolean} options.isLoading - Whether a fetch is already in progress.
 * @param {Function} options.onLoadMore - Called when the sentinel becomes visible.
 * @param {string} [options.rootMargin="100px"] - IntersectionObserver rootMargin.
 * @returns {{ sentinelRef: React.RefObject }}
 */
const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = "100px",
}) => {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, rootMargin]);

  return { sentinelRef };
};

export default useInfiniteScroll;
